import type { PatternHole, HoleType } from "@/types/pattern";

function deriveHoleType(holeId: string): HoleType {
  if (/^B\d/i.test(holeId)) return "batter";
  if (/^F\d/i.test(holeId)) return "buffer";
  if (/^T\d/i.test(holeId)) return "t-row";
  return "production";
}

export function parsePatternCSV(csvText: string): {
  patternId: string;
  holes: PatternHole[];
  errors: string[];
} {
  const errors: string[] = [];
  const holes: PatternHole[] = [];
  let patternId = "";

  const lines = csvText
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length === 0) {
    errors.push("CSV file is empty");
    return { patternId: "", holes: [], errors };
  }

  const seenHoleIds = new Set<string>();

  for (let i = 0; i < lines.length; i++) {
    const cols = lines[i].split(",").map((c) => c.trim());
    const rowNum = i + 1;

    if (cols.length < 13) {
      errors.push(`Row ${rowNum}: expected 13 columns, got ${cols.length}`);
      continue;
    }

    const rowPatternId = cols[0];
    if (i === 0) {
      patternId = rowPatternId;
    } else if (rowPatternId !== patternId) {
      errors.push(
        `Row ${rowNum}: pattern ID "${rowPatternId}" differs from first row "${patternId}"`
      );
    }

    const holeId = cols[1];
    if (seenHoleIds.has(holeId)) {
      errors.push(`Row ${rowNum}: duplicate hole ID "${holeId}"`);
      continue;
    }
    seenHoleIds.add(holeId);

    const nums = cols.slice(2).map(parseFloat);
    if (nums.some((n) => !isFinite(n))) {
      errors.push(`Row ${rowNum}: non-numeric value in columns 3-13`);
      continue;
    }

    const [
      collarEasting,
      collarNorthing,
      collarRL,
      toeEasting,
      toeNorthing,
      toeRL,
      designedDepth,
      subdrill,
      diameter,
      angle,
      chargeWeight,
    ] = nums;

    if (designedDepth <= 0) {
      errors.push(`Row ${rowNum}: designed depth must be positive (got ${designedDepth})`);
      continue;
    }

    holes.push({
      holeId,
      holeType: deriveHoleType(holeId),
      collarEasting,
      collarNorthing,
      collarRL,
      toeEasting,
      toeNorthing,
      toeRL,
      designedDepth,
      subdrill,
      diameter,
      angle,
      chargeWeight,
    });
  }

  return { patternId, holes, errors };
}
